<template>
  <Transition name="fade">
    <button
      v-if="isVisible"
      @click="scrollToTop"
      class="fixed bottom-6 right-6 z-[60] w-12 h-12 bg-gradient-to-br from-red-600 via-black to-green-600 hover:from-red-700 hover:via-gray-900 hover:to-green-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group border-2 border-white/20"
      aria-label="Back to top"
    >
      <svg
        class="w-5 h-5 transform group-hover:-translate-y-1 transition-transform duration-200"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 10l7-7m0 0l7 7m-7-7v18"
        />
      </svg>
    </button>
  </Transition>
</template>

<script>
import { onMounted, onUnmounted, ref } from "vue";

export default {
  name: "BackToTopButton",

  setup() {
    const isVisible = ref(false);

    const checkScrollPosition = () => {
      isVisible.value = window.pageYOffset > 300;
    };

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    onMounted(() => {
      window.addEventListener("scroll", checkScrollPosition);
      checkScrollPosition(); // Check initial position
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", checkScrollPosition);
    });

    return {
      isVisible,
      scrollToTop,
    };
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* Keyframes for organic fluid gradient animation */
@keyframes fluidGradient {
  0% {
    background: radial-gradient(
        circle at 25% 35%,
        #dc2626 0%,
        rgba(220, 38, 38, 0.6) 30%,
        transparent 60%
      ),
      radial-gradient(
        ellipse 60% 80% at 75% 15%,
        #1f2937 0%,
        rgba(31, 41, 55, 0.5) 35%,
        transparent 70%
      ),
      radial-gradient(
        ellipse 80% 40% at 15% 85%,
        #059669 0%,
        rgba(5, 150, 105, 0.4) 40%,
        transparent 65%
      ),
      conic-gradient(
        from 0deg at 50% 50%,
        #047857,
        #dc2626,
        #1f2937,
        #059669,
        #047857
      );
  }

  12% {
    background: radial-gradient(
        ellipse 70% 50% at 45% 60%,
        #dc2626 0%,
        rgba(220, 38, 38, 0.7) 25%,
        transparent 55%
      ),
      radial-gradient(
        circle at 85% 85%,
        #1f2937 0%,
        rgba(31, 41, 55, 0.6) 40%,
        transparent 75%
      ),
      radial-gradient(
        ellipse 40% 90% at 10% 20%,
        #059669 0%,
        rgba(5, 150, 105, 0.5) 35%,
        transparent 60%
      ),
      conic-gradient(
        from 45deg at 30% 70%,
        #047857,
        #1f2937,
        #dc2626,
        #059669,
        #047857
      );
  }

  25% {
    background: radial-gradient(
        ellipse 50% 70% at 80% 40%,
        #dc2626 0%,
        rgba(220, 38, 38, 0.5) 35%,
        transparent 70%
      ),
      radial-gradient(
        ellipse 90% 60% at 20% 75%,
        #1f2937 0%,
        rgba(31, 41, 55, 0.7) 30%,
        transparent 65%
      ),
      radial-gradient(
        circle at 65% 15%,
        #059669 0%,
        rgba(5, 150, 105, 0.6) 45%,
        transparent 80%
      ),
      conic-gradient(
        from 120deg at 80% 20%,
        #059669,
        #047857,
        #1f2937,
        #dc2626,
        #059669
      );
  }

  37% {
    background: radial-gradient(
        circle at 15% 55%,
        #dc2626 0%,
        rgba(220, 38, 38, 0.8) 20%,
        transparent 50%
      ),
      radial-gradient(
        ellipse 60% 80% at 90% 10%,
        #1f2937 0%,
        rgba(31, 41, 55, 0.4) 50%,
        transparent 85%
      ),
      radial-gradient(
        ellipse 85% 45% at 40% 90%,
        #059669 0%,
        rgba(5, 150, 105, 0.7) 25%,
        transparent 55%
      ),
      conic-gradient(
        from 200deg at 10% 90%,
        #1f2937,
        #059669,
        #047857,
        #dc2626,
        #1f2937
      );
  }

  50% {
    background: radial-gradient(
        ellipse 80% 60% at 60% 25%,
        #dc2626 0%,
        rgba(220, 38, 38, 0.6) 40%,
        transparent 75%
      ),
      radial-gradient(
        circle at 30% 80%,
        #1f2937 0%,
        rgba(31, 41, 55, 0.8) 35%,
        transparent 70%
      ),
      radial-gradient(
        ellipse 45% 85% at 85% 60%,
        #059669 0%,
        rgba(5, 150, 105, 0.5) 50%,
        transparent 80%
      ),
      conic-gradient(
        from 270deg at 90% 40%,
        #dc2626,
        #1f2937,
        #059669,
        #047857,
        #dc2626
      );
  }

  62% {
    background: radial-gradient(
        circle at 35% 70%,
        #dc2626 0%,
        rgba(220, 38, 38, 0.7) 30%,
        transparent 65%
      ),
      radial-gradient(
        ellipse 75% 55% at 10% 25%,
        #1f2937 0%,
        rgba(31, 41, 55, 0.6) 45%,
        transparent 80%
      ),
      radial-gradient(
        ellipse 65% 75% at 90% 90%,
        #059669 0%,
        rgba(5, 150, 105, 0.8) 20%,
        transparent 50%
      ),
      conic-gradient(
        from 320deg at 60% 10%,
        #059669,
        #dc2626,
        #047857,
        #1f2937,
        #059669
      );
  }

  75% {
    background: radial-gradient(
        ellipse 55% 90% at 85% 80%,
        #dc2626 0%,
        rgba(220, 38, 38, 0.5) 50%,
        transparent 85%
      ),
      radial-gradient(
        circle at 40% 15%,
        #1f2937 0%,
        rgba(31, 41, 55, 0.7) 25%,
        transparent 60%
      ),
      radial-gradient(
        ellipse 90% 50% at 20% 65%,
        #059669 0%,
        rgba(5, 150, 105, 0.6) 40%,
        transparent 75%
      ),
      conic-gradient(
        from 10deg at 25% 85%,
        #047857,
        #059669,
        #dc2626,
        #1f2937,
        #047857
      );
  }

  87% {
    background: radial-gradient(
        circle at 70% 45%,
        #dc2626 0%,
        rgba(220, 38, 38, 0.8) 35%,
        transparent 70%
      ),
      radial-gradient(
        ellipse 50% 70% at 95% 70%,
        #1f2937 0%,
        rgba(31, 41, 55, 0.5) 55%,
        transparent 90%
      ),
      radial-gradient(
        ellipse 80% 60% at 5% 30%,
        #059669 0%,
        rgba(5, 150, 105, 0.7) 30%,
        transparent 65%
      ),
      conic-gradient(
        from 330deg at 75% 25%,
        #1f2937,
        #047857,
        #dc2626,
        #059669,
        #1f2937
      );
  }

  100% {
    background: radial-gradient(
        circle at 25% 35%,
        #dc2626 0%,
        rgba(220, 38, 38, 0.6) 30%,
        transparent 60%
      ),
      radial-gradient(
        ellipse 60% 80% at 75% 15%,
        #1f2937 0%,
        rgba(31, 41, 55, 0.5) 35%,
        transparent 70%
      ),
      radial-gradient(
        ellipse 80% 40% at 15% 85%,
        #059669 0%,
        rgba(5, 150, 105, 0.4) 40%,
        transparent 65%
      ),
      conic-gradient(
        from 0deg at 50% 50%,
        #047857,
        #dc2626,
        #1f2937,
        #059669,
        #047857
      );
  }
}

/* Ensure button stays above other elements */
button {
  backdrop-filter: blur(10px);
  border-radius: 50%;
  /* Ensure perfect circle */
  /* Enhanced Kenyan flag-inspired styling with true fluid animation */
  background: radial-gradient(
      circle at 25% 35%,
      #dc2626 0%,
      rgba(220, 38, 38, 0.6) 30%,
      transparent 60%
    ),
    radial-gradient(
      ellipse 60% 80% at 75% 15%,
      #1f2937 0%,
      rgba(31, 41, 55, 0.5) 35%,
      transparent 70%
    ),
    radial-gradient(
      ellipse 80% 40% at 15% 85%,
      #059669 0%,
      rgba(5, 150, 105, 0.4) 40%,
      transparent 65%
    ),
    conic-gradient(
      from 0deg at 50% 50%,
      #047857,
      #dc2626,
      #1f2937,
      #059669,
      #047857
    );
  animation: fluidGradient 12s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  background-size: 300% 300%;
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3),
    /* Red glow */ 0 0 0 1px rgba(255, 255, 255, 0.2);
  /* White border accent */
}

button:hover {
  border-radius: 50%;
  /* Maintain perfect circle on hover */
  background: radial-gradient(
      circle at 25% 35%,
      #b91c1c 0%,
      rgba(185, 28, 28, 0.7) 25%,
      transparent 55%
    ),
    radial-gradient(
      ellipse 60% 80% at 75% 15%,
      #111827 0%,
      rgba(17, 24, 39, 0.6) 30%,
      transparent 65%
    ),
    radial-gradient(
      ellipse 80% 40% at 15% 85%,
      #047857 0%,
      rgba(4, 120, 87, 0.5) 35%,
      transparent 60%
    ),
    conic-gradient(
      from 0deg at 50% 50%,
      #065f46,
      #b91c1c,
      #111827,
      #047857,
      #065f46
    );
  animation: fluidGradient 6s cubic-bezier(0.25, 0.1, 0.25, 1) infinite;
  /* Faster and more dynamic on hover */
  background-size: 400% 400%;
  box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4),
    /* Enhanced red glow */ 0 0 0 2px rgba(255, 255, 255, 0.3);
  /* Enhanced white border */
  transform: translateY(-2px);
}

@media (max-width: 640px) {
  button {
    bottom: 1rem;
    right: 1rem;
    width: 2.75rem;
    height: 2.75rem;
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
}
</style>
